import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { COLORS } from '../utils/constants';

const { FiBook, FiChevronRight, FiX } = FiIcons;

const KnowledgeSection = () => {
  const [showManual, setShowManual] = useState(false);

  return (
    <div className="space-y-4">
      
      {/* User Manual Button */}
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="bg-white rounded-xl shadow-lg p-6 border-2 cursor-pointer"
        style={{ borderColor: COLORS.primary }}
        onClick={() => setShowManual(true)}
      >
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-full text-white" style={{ backgroundColor: COLORS.primary }}>
            <SafeIcon icon={FiBook} size={24} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold" style={{ color: COLORS.primary }}>
              Benutzerhandbuch
            </h3>
            <p className="text-sm text-gray-600">
              Ausführliche Anleitung in einfacher Sprache - Schritt für Schritt erklärt
            </p>
          </div>
          <SafeIcon icon={FiChevronRight} size={20} style={{ color: COLORS.primary }} />
        </div>
      </motion.div>

      {/* Manual Overlay */}
      <AnimatePresence>
        {showManual && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowManual(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
                <h2 className="text-2xl font-bold flex items-center gap-3" style={{ color: COLORS.primary }}>
                  <SafeIcon icon={FiBook} size={28} />
                  Benutzerhandbuch
                </h2>
                <button
                  onClick={() => setShowManual(false)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <SafeIcon icon={FiX} size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="prose prose-sm max-w-none space-y-6">
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-lg font-bold text-blue-900 mb-2">Schnellstart</h3>
                    <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                      <li><strong>Name eingeben:</strong> Tragen Sie oben Ihren Namen oder Firmennamen ein</li>
                      <li><strong>Objekt wählen:</strong> Wählen Sie "Wohnung" oder legen Sie ein neues Objekt an</li>
                      <li><strong>Zählerart wählen:</strong> Klicken Sie auf Strom, Gas oder Wasser</li>
                      <li><strong>Ablesung erfassen:</strong> Datum, Uhrzeit und Zählerstand eingeben</li>
                      <li><strong>Speichern:</strong> Klicken Sie auf den orangefarbenen Button</li>
                    </ol>
                  </div>

                  <h3 className="text-xl font-bold" style={{ color: COLORS.primary }}>Wichtige Hinweise</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                      <h4 className="font-bold text-orange-900">💾 Datenspeicherung (LocalStorage)</h4>
                      <p className="text-sm text-orange-800">
                        Ihre Daten werden lokal im Browser gespeichert (LocalStorage). Nutzen Sie regelmäßig den 
                        <strong> Magic Download</strong>, um Ihre Daten als PDF (Portable Document Format - Dokumentformat) 
                        und CSV (Comma-Separated Values - Kommagetrennte Werte) zu sichern.
                      </p>
                    </div>

                    <div className="bg-green-50 border-l-4 border-green-500 p-4">
                      <h4 className="font-bold text-green-900">📸 Foto-Funktion</h4>
                      <p className="text-sm text-green-800">
                        <strong>"Foto aufnehmen"</strong> öffnet die Kamera, 
                        <strong> "Foto auswählen"</strong> öffnet Ihre Galerie/Dateien (File Picker - Dateiauswahl). 
                        Das Foto bleibt auf Ihrem Gerät und wird nicht hochgeladen. Sie können im Foto zoomen und es verschieben.
                      </p>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                      <h4 className="font-bold text-blue-900">⚡ Dezimalstellen-Regeln</h4>
                      <p className="text-sm text-blue-800">
                        <strong>Strom:</strong> 1 Nachkommastelle (z.B. 123,4 kWh)<br/>
                        <strong>Gas:</strong> 3 Nachkommastellen (z.B. 123,456 m³)<br/>
                        <strong>Wasser:</strong> 3 Nachkommastellen (z.B. 123,456 m³)
                      </p>
                    </div>

                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                      <h4 className="font-bold text-purple-900">🔥 Gas-Umrechnung</h4>
                      <p className="text-sm text-purple-800">
                        Für Gas benötigen Sie <strong>Zustandszahl (z-Zahl)</strong> und <strong>Brennwert</strong> 
                        von Ihrer Gasrechnung. Ohne diese Werte kann kein kWh-Verbrauch berechnet werden.<br/>
                        <strong>Formel:</strong> kWh = (m³ neu - m³ alt) × Zustandszahl × Brennwert
                      </p>
                    </div>

                    <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4">
                      <h4 className="font-bold text-indigo-900">📊 Zeitraum-Analyse</h4>
                      <p className="text-sm text-indigo-800">
                        Wählen Sie zwei Ablesungen aus, um den Verbrauch zwischen diesen Zeitpunkten zu berechnen. 
                        Sie erhalten Gesamtverbrauch, Durchschnitt pro Tag und 365-Tage-Prognose.
                        <br/><strong>Wenn keine Auswahl:</strong> Das Tool nutzt automatisch die letzten zwei Ablesungen.
                      </p>
                    </div>

                    <div className="bg-red-50 border-l-4 border-red-500 p-4">
                      <h4 className="font-bold text-red-900">🔄 CSV Import/Export</h4>
                      <p className="text-sm text-red-800">
                        Exportieren Sie Ihre Daten als CSV, um sie in Excel zu öffnen oder auf einem anderen 
                        Gerät zu importieren. Der Import fügt Daten hinzu, ohne bestehende zu löschen.
                      </p>
                    </div>

                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                      <h4 className="font-bold text-yellow-900">📅 Alte Ablesungen nachtragen</h4>
                      <p className="text-sm text-yellow-800">
                        Sie können Ablesungen mit altem Datum nachtragen. Das Tool sortiert automatisch alle 
                        Ablesungen nach Datum/Zeit (ISO 8601 - Zeitformat intern), sodass die Berechnungen korrekt bleiben.
                      </p>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mt-6" style={{ color: COLORS.primary }}>
                    Häufige Probleme
                  </h3>

                  <div className="space-y-3">
                    <details className="bg-gray-50 rounded-lg p-4">
                      <summary className="font-bold cursor-pointer text-gray-900">
                        Problem: Zeitraum zeigt ungültige Werte oder "0 Tage"
                      </summary>
                      <p className="text-sm text-gray-700 mt-2">
                        <strong>Ursache:</strong> Die beiden Ablesungen haben das gleiche Datum/Uhrzeit oder liegen zu nah beieinander.<br/>
                        <strong>Lösung:</strong> Wählen Sie Ablesungen mit zeitlichem Abstand (z.B. eine Woche oder mehr).
                      </p>
                    </details>

                    <details className="bg-gray-50 rounded-lg p-4">
                      <summary className="font-bold cursor-pointer text-gray-900">
                        Problem: Bei Gas steht "—" beim Verbrauch
                      </summary>
                      <p className="text-sm text-gray-700 mt-2">
                        <strong>Ursache:</strong> Zustandszahl oder Brennwert fehlen oder sind 0.<br/>
                        <strong>Lösung:</strong> Tragen Sie beim Erfassen der Ablesung die Werte von Ihrer 
                        Gasrechnung ein. Diese sind erforderlich für die Umrechnung von m³ in kWh.
                      </p>
                    </details>

                    <details className="bg-gray-50 rounded-lg p-4">
                      <summary className="font-bold cursor-pointer text-gray-900">
                        Problem: Daten sind nach Browser-Update weg
                      </summary>
                      <p className="text-sm text-gray-700 mt-2">
                        <strong>Ursache:</strong> Browser-Cache wurde gelöscht.<br/>
                        <strong>Lösung:</strong> Importieren Sie Ihre letzte CSV-Sicherung über "CSV importieren". 
                        <strong> Tipp:</strong> Erstellen Sie regelmäßig Backups mit Magic Download!
                      </p>
                    </details>

                    <details className="bg-gray-50 rounded-lg p-4">
                      <summary className="font-bold cursor-pointer text-gray-900">
                        Problem: Foto wird auf Android nicht richtig angezeigt
                      </summary>
                      <p className="text-sm text-gray-700 mt-2">
                        <strong>Lösung:</strong> Verwenden Sie die Zoom-Buttons (+/-) und ziehen Sie das Foto 
                        mit dem Finger, um es zu verschieben. Das Foto wird mit "object-fit: contain" angezeigt, 
                        sodass es vollständig sichtbar ist.
                      </p>
                    </details>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
                    <h4 className="font-bold text-green-900 mb-2">✅ Vollständiges Handbuch</h4>
                    <p className="text-sm text-green-800">
                      Das vollständige, sehr ausführliche Benutzerhandbuch finden Sie in der Datei 
                      <code className="bg-white px-2 py-1 rounded mx-1">docs/benutzerhandbuch.md</code> 
                      des Projekts. Es ist in sehr einfacher Sprache (6. Klasse) geschrieben und erklärt 
                      jeden Schritt im Detail.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <button
                  onClick={() => setShowManual(false)}
                  className="w-full py-3 px-6 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  Schließen
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default KnowledgeSection;